/**
 * The bake manifest schema (AD-004): `data/baked/manifest.json` describes
 * every baked asset plus, honestly, every real game-data type this
 * pipeline does not yet bake (see `tools/bake/src/unbaked.ts`). No
 * timestamps anywhere — content hashes make the manifest itself
 * reproducible, so `version` is the only non-content field.
 */

/** The asset types this pipeline currently bakes. */
export const ASSET_CATEGORIES = ["palettes", "screens", "sprites", "fonts", "models", "music", "sfx", "world"] as const;
export type AssetCategory = (typeof ASSET_CATEGORIES)[number];

export const MANIFEST_VERSION = 1;

/**
 * `"model-sprite"` is a *declaration*, like `"script"`, not a heuristic: the
 * sheet is one a `.TBL`'s sprite nodes index into (docs/formats/models-tbl.md
 * §4.3), so it is drawn by the 3D flight renderer and takes that renderer's
 * palette. `"flight"` is the same class one step wider — a sheet `RB.EXE`
 * itself blits while the sim is up, without a `.TBL` node or a `.TTM` naming
 * it. See `matchPalette`'s doc comment for the ordering.
 *
 * `"proximity"` no longer appears in any bake and cannot: the rule that
 * produced it now throws (G28). The member is kept so an *old* manifest still
 * parses and so the tests that describe the retired rule can name it.
 */
export type PaletteMatchMethod =
  | "script"
  | "model-sprite"
  | "flight"
  | "shell"
  | "exact"
  | "prefix"
  | "proximity"
  | "default";

/** How an image (screen or sprite sheet) got paired with the palette it's baked with — see `palette-match.ts`. */
export interface PaletteMatchMeta {
  readonly palette: string;
  readonly method: PaletteMatchMethod;
  readonly detail?: string;
}

export interface PaletteMeta {
  readonly entries: number;
}

export interface ScreenMeta {
  readonly width: number;
  readonly height: number;
  readonly paletteMatch: PaletteMatchMeta;
}

export interface SpriteFrameMeta {
  readonly index: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface SpriteMeta {
  readonly frameCount: number;
  readonly sheetWidth: number;
  readonly sheetHeight: number;
  readonly frames: readonly SpriteFrameMeta[];
  readonly paletteMatch: PaletteMatchMeta;
}

export interface GlyphMeta {
  readonly code: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface FontMeta {
  readonly fontWidth: number;
  readonly fontHeight: number;
  readonly baseLine: number;
  readonly startSymbol: number;
  readonly numSymbols: number;
  readonly glyphs: readonly GlyphMeta[];
}

/** One LOD's mesh summary within a baked model object (see `model-bake.ts` for the full mesh body this summarizes). */
export interface ModelLodMeta {
  readonly index: number;
  /** §3: apparent-size/detail switching threshold (INFERRED meaning; 255 = "always"). */
  readonly detail: number;
  /** §3: second switching metric (INFERRED, always `256n + 64`). */
  readonly range: number;
  readonly vertexCount: number;
  readonly triangleCount: number;
  /** 2-point faces (§4.4): roads, rivers, struts, rigging. 48 corpus objects are made of nothing else. */
  readonly lineCount: number;
  /** Screen-space sprite billboards from §4.3 node types 2/3, summed over every state. */
  readonly billboardCount: number;
  /** Shaded spheres from §4.3 node type 1 (pilot heads, tree canopies), summed over every state. */
  readonly sphereCount: number;
}

/**
 * The `.BMP` sheet a model's sprite `frameIndex` values address, resolved at
 * bake time so the baked model JSON is self-contained. VERIFIED name pairing
 * — see `spriteSheetNameFor` in `packages/formats/src/tbl-sprite.ts`.
 */
export interface ModelSpriteSheet {
  /** Source sheet name, e.g. `"FLAK.BMP"`. */
  readonly sheet: string;
  /** Manifest id of the baked sprite entry, e.g. `"sprites/FLAK.BMP"`. */
  readonly spriteId: string;
  /** Path of the baked PNG relative to `data/baked/`, e.g. `"sprites/FLAK.BMP.png"`. */
  readonly path: string;
  readonly sheetWidth: number;
  readonly sheetHeight: number;
  readonly frames: readonly SpriteFrameMeta[];
}

/**
 * One screen-space sprite in a baked LOD. The engine blits the frame at
 * 1:1 pixel size with its top-left at `project(position) + (offsetX,
 * offsetY)` in 320×200 screen pixels — no perspective scaling; distance is
 * expressed by which LOD (hence which frame) gets picked.
 */
export interface ModelBillboard {
  /** Anchor in metres, model space (Z up, +Y nose). */
  readonly position: readonly [number, number, number];
  readonly offsetX: number;
  readonly offsetY: number;
  readonly frameIndex: number;
  /** Frame rect within the object's `spriteSheet` PNG; absent if the model has no paired sheet. */
  readonly frame?: SpriteFrameMeta;
  /** `false` for a node-type-2 sprite, which the engine anchors at the object origin. */
  readonly anchoredToVertex: boolean;
  readonly nodeType: 2 | 3;
  readonly stateChannel: number;
  readonly stateIndex: number;
}

/** One shaded sphere in a baked LOD (§4.3 node type 1). */
export interface ModelSphere {
  /** Centre in metres, model space. */
  readonly position: readonly [number, number, number];
  /** Radius in metres. */
  readonly radius: number;
  /** The 256-colour palette index the engine reads verbatim (no shade offset — see `model-bake-extras.ts`). */
  readonly colour: number;
  /** The low-colour adapter's index, which the engine masks with `0x1F`. */
  readonly altColour: number;
  readonly colorRgba: readonly [number, number, number, number];
  readonly stateChannel: number;
  readonly stateIndex: number;
}

/** One LOD's line-segment geometry — endpoint pairs, one colour per segment. */
export interface ModelLodLines {
  /** Flat x,y,z triplets in metres. */
  readonly positions: readonly number[];
  /** Flat endpoint indices into `positions`, 2 per segment. */
  readonly indices: readonly number[];
  /** One entry per segment. */
  readonly colors: readonly ModelFaceColor[];
}

/**
 * One face's colour: the raw `.TBL` bytes (the audit trail — see
 * docs/formats/shading-and-paint.md §11 for what each one actually is),
 * the resolved RGBA at the reference attitude, and the three fields a
 * renderer needs to re-shade the face itself as the aircraft banks.
 */
export interface ModelFaceColor {
  /** Raw byte +1 — the VGA **outline** colour, before the `.CLG` repaint. */
  readonly front: number;
  /** Raw byte +2 — the VGA **fill** colour, before the `.CLG` repaint. */
  readonly back: number;
  /** Raw byte +3 — the 16-colour EGA/CGA outline colour, dead in this VGA-only build. */
  readonly shadeFront: number;
  /** Raw byte +4 — the 16-colour EGA/CGA fill colour, dead in this VGA-only build. */
  readonly shadeBack: number;
  /** The colour the original draws this face with at the reference attitude (`model-paint.ts`'s `REFERENCE_SUN`). */
  readonly colorRgba: readonly [number, number, number, number];
  /**
   * The face's **unshaded** palette index after the paint step — the anchor
   * of the ramp shading walks. A runtime that re-shades a face computes
   * `anchorIndex + shadeDelta[halfWidth - 1][level]` itself.
   */
  readonly anchorIndex: number;
  /** `SHADES.DAT[anchorIndex]`: the shading half-width, `0` for a colour the engine never shades. */
  readonly halfWidth: number;
  /** `op & 0x80 && op & 0x20` — the engine shades this face's fill by the face normal. */
  readonly shadeable: boolean;
  /** Present only when the original draws a separate outline polyline, i.e. the outline colour differs from the fill. */
  readonly outlineRgba?: readonly [number, number, number, number];
  /**
   * The `.CLG` paint group that repainted this face (0 main fabric, 1 second
   * fabric/trim, 2 wings, 3 wing underside, 4 fuselage, 5 cowling), absent on
   * a face the repaint does not touch.
   *
   * `anchorIndex` alone cannot be inverted back to a group — most fighters
   * paint several groups the same index — so this is what a runtime needs to
   * repaint a model it has already loaded. It is what the player's paint shop
   * drives (docs/formats/shading-and-paint.md §10).
   */
  readonly paintGroup?: number;
}

/**
 * How a baked model's colour bytes were turned into `colorRgba`. Which one
 * an object gets is decided per object by `model-bake.ts`'s
 * `colourMethodFor`, and both are evidenced:
 *
 * - `"paint-shade"` — the original's own pipeline for flying objects
 *   (docs/formats/shading-and-paint.md, VERIFIED by disassembly + live
 *   capture): fill = byte +2, overwritten by the aircraft's paint scheme on
 *   every face its `.CLG` lists, then moved along its `SHADES.DAT` ramp by
 *   the quantised Lambert term. It replaces the withdrawn
 *   `"index-plus-shade"` (`palette[front + shadeFront]`), which accounted
 *   for 23.3 %/1.8 %/0.0 % of the model pixels in three live captures where
 *   this one accounts for 100 %.
 * - `"raw-index"` — `palette[front]`, for scenery (everything modelled
 *   standing on its own ground plane). Evidence: measured off the running
 *   original under DOSBox-X — Verdun's rivers/roads/fields render at exactly
 *   `colourFront`, flat. See docs/formats/world-terrain.md's ground-rendering
 *   section.
 */
export type ModelColorMethod = "paint-shade" | "raw-index";

/** One selectable paint scheme of a fighter: the factory scheme, or one named ace's personal aircraft. */
export interface ModelPaintOption {
  /** `"Default"` or the ace's surname (`"Richthofen"`). */
  readonly label: string;
  /** Manifest id of the model baked with this scheme — `models/FOKDR1` or `models/FOKDR1@Richthofen`. */
  readonly id: string;
  /** That entry's path relative to `data/baked/`. */
  readonly path: string;
  /** The six palette indices this scheme paints, in `.CLG` group order. */
  readonly colours: readonly number[];
}

/**
 * The load-time `.CLG` repaint this model was baked with
 * (docs/formats/shading-and-paint.md §4). Present only on the 22 fighters:
 * every other model ships its real colours in the `.TBL`.
 *
 * `options` is the same list on every entry of a family — the default and
 * each ace variant — so a viewer or a loader can offer the whole set from
 * whichever one it happens to hold.
 */
export interface ModelPaintMeta {
  /** Model type id: the position of this model's name in `RB.EXE`'s own name list (`"fokdr1"` = 21). */
  readonly typeId: number;
  /** That name, as the executable spells it. */
  readonly typeName: string;
  /** Which of `options` this entry is — `"Default"` or an ace surname. */
  readonly scheme: string;
  /** Ace id in `RB.EXE`'s 25-name table, on an ace variant only. */
  readonly aceId?: number;
  /** The six palette indices applied, in `.CLG` group order. */
  readonly colours: readonly number[];
  /** How many face records the repaint rewrote (the `.CLG`'s total offset count). */
  readonly paintedFaces: number;
  /**
   * Face records that still carry a colour-key marker after the repaint —
   * `0` for every fighter but the three with shipped art bugs (`FOKD8` 4,
   * `HALBD2` 4, `SE5` 1; §4.1). Anything else means a `.CLG` did not load.
   */
  readonly markerResidues: number;
  /** Every scheme baked for this model, the default first. */
  readonly options: readonly ModelPaintOption[];
}

export interface ModelColorMapping {
  readonly palette: string;
  readonly method: ModelColorMethod;
  readonly inferred: boolean;
  readonly evidence: string;
}

export interface ModelMeta {
  /** Stable directory-slot index within the source `.TBL` (what a `.WLD` placement or a `.CLG` damage entry addresses this object by). */
  readonly objectIndex: number;
  /** Total object count in the source `.TBL` file — 1 for a standalone aircraft, >1 for a library (`TERR1.TBL`, `MISC.TBL`, `PTPLANE.TBL`, `FLAK.TBL`). */
  readonly objectCount: number;
  /**
   * Object header's `boundingRadius` (§2), converted to metres at this
   * object's own `2^scaleExponent / 20 ft` (gi-format.md §6.1) — ÷65 only
   * for the common `scaleExponent === 0` case (every aircraft; most
   * buildings). "Notes for the port"'s flat ÷65 rule is the `s = 0` special
   * case, not the general one.
   */
  readonly boundingRadius: number;
  /** The low byte of `classFlags` this model was baked at — see `BakedModel.scaleExponent` (`model-bake.ts`) for the full explanation. */
  readonly scaleExponent: number;
  readonly lods: readonly ModelLodMeta[];
  readonly colorMapping: ModelColorMapping;
  /** The paint scheme this entry was baked with; absent for every model the engine never repaints. */
  readonly paint?: ModelPaintMeta;
  /** The paired `.BMP` sheet this object's sprite frame indices address; absent for the 356 objects with no sprite nodes. */
  readonly spriteSheet?: ModelSpriteSheet;
  /**
   * `true` when the object has **no triangles at any LOD** and is drawn
   * entirely from sprites and/or line segments — 55 of the corpus's 360
   * objects. Flagged in the manifest because such an object looks broken to
   * anything that only counts triangles (`FLAK`, `EXPLO`, `SMOKE` and the
   * `TERR*` road/river polylines all read as "0 tri, every LOD").
   */
  readonly spriteBased?: boolean;
}

/**
 * How a music/sfx entry was voiced. The first two name the `.SNG` device
 * track alone (docs/formats/sound-music.md — `SngDevice`'s `Mt32`/`AdLib`)
 * rendered through a general-purpose synthesiser; `adlib-custom-bank` means
 * the AdLib track was played through an emulated OPL2 loaded with the game's
 * own instrument bank (`SXCODE2.OVL`'s `003:` chunk), which is the only
 * combination that reproduces the original gunfire/engine timbres rather
 * than turning them into musical notes.
 */
export type AudioDevice = "mt32" | "adlib" | "adlib-custom-bank";

/**
 * Manifest metadata for a `music`/`sfx` entry (`tools/bake/src/audio-stage.ts`).
 * `sha256` (on `BakeManifestEntry`, not here) hashes the actual `.ogg` bytes
 * on disk, same as every other category — but ffmpeg's Ogg muxer embeds a
 * random per-stream serial number, so re-baking the identical `.mid` still
 * produces different `.ogg` bytes/hash across runs even though the decoded
 * audio is identical (verified empirically: fluidsynth's WAV render *is*
 * byte-stable; only the Ogg container framing varies). `midSha256` hashes
 * the deterministic intermediate `.mid` this entry was rendered from — the
 * real reproducibility anchor — and `hashDerived: true` flags that this
 * entry's own `sha256` is not one (see docs/engineering/audio-plan.md).
 */
export interface AudioMeta {
  /** Length of the source event stream in seconds — what the `.SNG` script itself spans. */
  readonly durationSeconds: number;
  readonly device: AudioDevice;
  readonly channels: number;
  readonly noteCount: number;
  readonly midSha256: string;
  readonly hashDerived: boolean;
  /**
   * `adlib-custom-bank` only: length of the actual rendered audio, which
   * exceeds `durationSeconds` by the envelope release tail (and, for a cue
   * whose script ends mid-note, by the declared hold window).
   */
  readonly renderedSeconds?: number;
  /**
   * `adlib-custom-bank` only: sha256 of the rendered PCM before Ogg encoding.
   * Stronger than `midSha256` as a reproducibility anchor — our OPL2 renderer
   * is pure integer DSP, so this pins the synthesis as well as the notes.
   */
  readonly pcmSha256?: string;
  /**
   * `adlib-custom-bank` only: the script ended with a note still held, so its
   * real length is decided by the game at runtime rather than by the resource
   * (see `adlib-render.ts`'s `ADLIB_HELD_NOTE_SECONDS`).
   */
  readonly sustainedAtEnd?: boolean;
}

/** A placement's world-space bounding box, in the same `.WLD` int32 world units as `x`/`y` (docs/formats/world-terrain.md). */
export interface WorldBoundsMeta {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

/**
 * Manifest metadata for a `world` entry (`tools/bake/src/stages/world.ts`) — one baked region
 * (or the practice range) decoded from a `.WLD`/`.TBL` pair. See
 * docs/formats/world-terrain.md for the full evidence behind every field below.
 */
export interface WorldMeta {
  /** `"London" | "Dunkirk" | "Somme" | "Paris" | "Verdun" | "Practice Range"`. */
  readonly region: string;
  /**
   * Whether the `TERRn` <-> region link is an independently VERIFIED fact or the INFERRED
   * (strong) naming-convention argument documented in world-terrain.md §2.3 — always `true`
   * for the five campaign regions today (no further RE has closed this to VERIFIED yet).
   * `false` for the practice range, which isn't paired to a campaign map at all.
   */
  readonly regionPairingInferred: boolean;
  readonly sourceWld: string;
  readonly sourceTbl: string;
  readonly placementCount: number;
  /** Placements whose `objectId` resolved to a baked model (world-terrain.md §1.1). */
  readonly resolvedCount: number;
  /** Placements whose `objectId` names an unused/dummy `.TBL` directory slot — not a decode error (world-terrain.md §1.1). */
  readonly unresolvedCount: number;
  readonly bounds: WorldBoundsMeta;
  readonly aerodromeCount: number;
  /** Distinct `objectId`s with a ground-collision surface (gi-format.md), keyed in the body's `groundObjects`. 0 for a region with no paired `.GI` file. */
  readonly groundObjectCount: number;
}

export type AssetMeta = PaletteMeta | ScreenMeta | SpriteMeta | FontMeta | ModelMeta | AudioMeta | WorldMeta;

export interface BakeManifestEntry {
  readonly id: string;
  readonly category: AssetCategory;
  /** Path to the baked artifact, relative to `data/baked/` (what the `/assets` viewer fetches under `/baked/`). */
  readonly path: string;
  /** Human-readable provenance of the original bytes, e.g. `"VOLUME.003/MAINMENU.SCR"` or `"loose/SYSTEM.FNT"`. */
  readonly sourcePath: string;
  readonly sha256: string;
  readonly meta: AssetMeta;
}

/** A real game-data asset this pipeline does not (yet) bake — see `tools/bake/src/unbaked.ts`. */
export interface UnbakedEntry {
  readonly id: string;
  /** File extension without the dot, e.g. `"TTM"`. */
  readonly type: string;
  readonly sourcePath: string;
  readonly reason: string;
}

export interface BakeManifest {
  readonly version: number;
  readonly entries: readonly BakeManifestEntry[];
  readonly unbaked: readonly UnbakedEntry[];
}
